import { useLayoutStore } from '@/store';
import { getAppId, getToken } from '../utils';
import { post } from '../http/request';
import { useUrlBuilder } from './useUrl';
import { completeUploadProgressError, completeUploadProgressSuccess, finishVideoCompress, getUploadFileNameFromParams, onAxiosUploadProgress, onVideoCompressPhaseChange, onVideoCompressProgress, startUploadProgress, startVideoCompress } from './useUploadProgress';
import { compressVideo, shouldCompressVideo } from '../utils/videoCompress';
export const prepareUploadFile = async file => {
  if (!shouldCompressVideo(file)) {
    return file;
  }
  startVideoCompress(file.name);
  try {
    const compressed = await compressVideo(file, {
      onProgress: onVideoCompressProgress,
      onPhaseChange: onVideoCompressPhaseChange
    });
    finishVideoCompress(compressed.name);
    return compressed;
  } catch (error) {
    finishVideoCompress(file.name);
    console.warn('视频压缩失败，将上传原文件', error);
    return file;
  }
};
export const useUpload = () => {
  const {
    buildGetUrl,
    requestUrl
  } = useUrlBuilder();
  /**
   * 解析返回的上传文件数据
   * @param {String} jsonData 上传文件数据，[{name, downloadUri, filename}]
   * @param {Object} params 上传文件的参数
   * @returns {Array} 上传文件信息，[{name, downloadUri, filename, url}]
   */
  const parseUploadData = (jsonData, params) => {
    let pathList = [];
    if (jsonData != null) {
      try {
        pathList = JSON.parse(jsonData);
      } catch (e) {
        console.error(e);
      }
    }
    return Array.isArray(pathList) ? pathList.map(item => {
      const downloadParams = {
        ...params
      };
      downloadParams.filename = item.filename;
      return {
        ...item,
        url: getUploadFileUrl(item, downloadParams)
      };
    }) : [];
  };
  /**
   * 获得上传文件url
   * @param {*} item 上传文件
   * @param {*} params 上传文件的参数
   */
  const getUploadFileUrl = (item, params) => {
    if (item == null || item.downloadUri == null) {
      return null;
    } else {
      const currentMenuId = useLayoutStore().currentMenuId;
      const query = {
        ...params
      };
      query.Authorization = getToken();
      query.MenuId = currentMenuId;
      query.AppCode = getAppId();
      return buildGetUrl(item.downloadUri, query);
    }
  };
  const getUploadHeaders = computed(() => {
    const token = getToken();
    const appId = getAppId();
    const currentMenuId = useLayoutStore().currentMenuId;
    const header = {
      Authorization: token,
      MenuId: currentMenuId
    };
    if (appId) header.AppCode = appId;
    return header;
  });
  /**
   * 获得上传接口
   * @param {*} url 上传路径
   */
  const getUploadActionUrl = url => {
    return buildGetUrl(url, null);
  };
  /**
   * 上传文件
   * @param {*} url 请求的url
   * @param {*} params 请求参数
   */
  const fetchUpload = async (url, params) => {
    const preparedParams = {
      ...params
    };
    for (const key of Object.keys(preparedParams)) {
      const value = preparedParams[key];
      if (value instanceof File) {
        preparedParams[key] = await prepareUploadFile(value);
        break;
      }
    }
    startUploadProgress(getUploadFileNameFromParams(preparedParams));
    return new Promise((resolve, reject) => {
      post(requestUrl(url), {}, {
        showError: true,
        showMask: false
      }, {
        data: preparedParams,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onAxiosUploadProgress,
        transformRequest: [function (data) {
          const formData = new FormData();
          Object.keys(data).map(key => {
            formData.append(key, data[key]);
          });
          return formData;
        }]
      }).then(res => {
        if (res.data && res.success) {
          completeUploadProgressSuccess();
          resolve(res.data);
        } else {
          completeUploadProgressError();
          reject(res);
        }
      }).catch(e => {
        completeUploadProgressError();
        reject(e);
      });
    });
  };
  /**
   * 获得上传文件url列表
   * @param {*} jsonData 上传文件数据，[{name, downloadUri, filename}]
   * @param {*} params 上传文件的参数
   * @returns {Array} 文件url列表
   */
  const getPictureList = (jsonData, params) => {
    const tempList = parseUploadData(jsonData, params);
    if (Array.isArray(tempList)) {
      return tempList.map(item => item.url);
    } else {
      return [];
    }
  };
  /**
   * 将选中文件信息格式化成json信息
   * @param {Array} fileList 上传文件列表，[{name, fileUrl, data}]
   */
  const fileListToJson = fileList => {
    if (Array.isArray(fileList)) {
      // 多选/批量上传时，上传中的条目可能还没有 response，需做兼容处理
      const normalized = fileList.map(item => {
        const respData = item?.response?.data;
        const downloadUri = item?.downloadUri || respData?.downloadUri;
        const filename = item?.filename || respData?.filename;
        const uploadPath = item?.uploadPath || respData?.uploadPath;
        // 上传未完成的条目不落库，避免 undefined 读取和脏数据
        if (!downloadUri || !filename) return null;
        return {
          name: item?.name,
          downloadUri,
          filename,
          uploadPath
        };
      }).filter(Boolean);
      return JSON.stringify(normalized);
    } else {
      return undefined;
    }
  };
  return {
    getUploadFileUrl,
    parseUploadData,
    getUploadHeaders,
    getUploadActionUrl,
    prepareUploadFile,
    fetchUpload,
    getPictureList,
    fileListToJson
  };
};